/**
 * Reseller/commercial partner-console React Query hooks.
 *
 * `usePartnerMe()` hits `/v1/partner/me` (IsPartnerUser-gated) — a 403 for
 * non-partner users is surfaced as an error so the route guard can bounce
 * them to /dashboard. Partners create + list their own attributed orgs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiPaths } from "./api";
import { tokenStore } from "./tokenStore";
import type {
  PartnerCommercials,
  PartnerCommissions,
  PartnerOrg,
  PartnerProfile,
} from "./types";

const PARTNER_ME_KEY = ["partner-me"] as const;
const PARTNER_ORGS_KEY = ["partner-orgs"] as const;

/** Extended PartnerProfile with reseller settings fields returned by the
 *  backend since the settings endpoint expansion. Defined here (not in
 *  types.ts) per the no-edit-types rule. */
export type PartnerProfileExtended = PartnerProfile & {
  logo_url?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    postal_code?: string;
    state?: string;
    country?: string;
  };
  tax_id?: string;
  partner_role?: "administrator" | "manager" | "billing" | "";
  is_reseller?: boolean;
};

export type PartnerRole = "administrator" | "manager" | "billing";

export type PartnerMember = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: PartnerRole;
  is_self: boolean;
};

export type PartnerPendingInvite = {
  id: string;
  email: string;
  role: PartnerRole;
  invited_at: string;
};

export function usePartnerMe() {
  return useQuery<PartnerProfileExtended>({
    queryKey: PARTNER_ME_KEY,
    queryFn: async () =>
      (await api.get<PartnerProfileExtended>(ApiPaths.partnerMe)).data,
    enabled: !!tokenStore.getAccess(),
    retry: false,
    staleTime: 60_000,
  });
}

/** All fields a reseller can update on their own profile. */
export type PartnerSettingsInput = {
  name?: string;
  contact_email?: string;
  contact_phone?: string;
  tax_id?: string;
  invoice_prefix?: string;
  logo_url?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    postal_code?: string;
    state?: string;
    country?: string;
  };
};

/** Reseller self-service settings (PATCH /partner/me). */
export function useUpdatePartnerSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PartnerSettingsInput) =>
      (await api.patch<PartnerProfileExtended>(ApiPaths.partnerMe, patch)).data,
    onSuccess: (data) => qc.setQueryData(PARTNER_ME_KEY, data),
  });
}

/** Reseller logo upload (POST /partner/me/logo, multipart). */
export function useUploadPartnerLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("logo", file);
      return (
        await api.post<PartnerProfileExtended>(ApiPaths.partnerMeLogo, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data;
    },
    onSuccess: (data) => qc.setQueryData(PARTNER_ME_KEY, data),
  });
}

export function usePartnerOrgs(enabled = true) {
  return useQuery<PartnerOrg[]>({
    queryKey: PARTNER_ORGS_KEY,
    queryFn: async () =>
      (await api.get<PartnerOrg[]>(ApiPaths.partnerOrgs)).data,
    enabled: enabled && !!tokenStore.getAccess(),
  });
}

/** Soft-delete (remove from the book): cancels the customer's
 * subscription + stops commission. The row drops out of the list. */
export function useSoftDeletePartnerOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) =>
      (await api.delete(ApiPaths.partnerOrgDelete(orgId))).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PARTNER_ORGS_KEY });
    },
  });
}

/** Edit an existing org's commercial terms (per-session price, bundles)
 *  + company identity. PATCH — only the provided fields change. */
export function useUpdatePartnerOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      patch,
    }: {
      orgId: string;
      patch: UpdatePartnerOrgInput;
    }) => (await api.patch<PartnerOrg>(ApiPaths.partnerOrgUpdate(orgId), patch)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PARTNER_ORGS_KEY });
    },
  });
}

/** Provision a plan for one of the reseller's managed orgs (free /
 *  standard / pro). Flips the subscription FK server-side and marks any
 *  open plan-change request handled. */
export function useSetPartnerOrgPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      planSlug,
    }: {
      orgId: string;
      planSlug: string;
    }) =>
      (
        await api.post(ApiPaths.partnerOrgSetPlan(orgId), {
          plan_slug: planSlug,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PARTNER_ORGS_KEY });
    },
  });
}

/** The reseller's own NexBasira invoices (Model B wholesale — what the
 *  reseller owes NexBasira). Empty for commission resellers / commercials. */
export function usePartnerInvoices(page = 1, pageSize = 30) {
  return useQuery<import("./types").Paginated<import("./types").BillingInvoice>>({
    queryKey: ["partner-invoices", page, pageSize],
    queryFn: async () =>
      (
        await api.get<
          import("./types").Paginated<import("./types").BillingInvoice>
        >(ApiPaths.partnerInvoices, {
          params: { page: String(page), page_size: String(pageSize) },
        })
      ).data,
    enabled: !!tokenStore.getAccess(),
    placeholderData: (prev) => prev,
  });
}

export function usePartnerCommissions(enabled = true) {
  return useQuery<PartnerCommissions>({
    queryKey: ["partner-commissions"],
    queryFn: async () =>
      (await api.get<PartnerCommissions>(ApiPaths.partnerCommissions)).data,
    enabled: enabled && !!tokenStore.getAccess(),
  });
}

export function usePartnerCommercials(enabled = true) {
  return useQuery<PartnerCommercials>({
    queryKey: ["partner-commercials"],
    queryFn: async () =>
      (await api.get<PartnerCommercials>(ApiPaths.partnerCommercials)).data,
    enabled: enabled && !!tokenStore.getAccess(),
  });
}

export type UpdateCommercialTermsInput = {
  id: string;
  // Profile — individual vs company identity + contact + address.
  name?: string;
  email?: string;
  phone?: string;
  is_company?: boolean;
  legal_name?: string;
  registration_number?: string;
  vat_id?: string;
  address_line?: string;
  city?: string;
  postal_code?: string;
  address_country?: string;
  // Commission terms.
  commission_model?: "percentage" | "fixed";
  rate_base_pct?: string;
  rate_session_pct?: string;
  rate_signature_pct?: string;
  fixed_base_eur?: string;
  fixed_session_eur?: string;
  fixed_signature_eur?: string;
  commission_duration?: string;
  status?: string;
};

/** Reseller edits one of its own commercials — profile + commission terms. */
export function useUpdateCommercialTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateCommercialTermsInput) =>
      (await api.patch(ApiPaths.partnerCommercialUpdate(id), body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-commercials"] });
    },
  });
}

/** Reseller permanently deletes one of its own commercials. Succeeds with
 *  200; the backend answers 409 `{detail, pending_eur, code:"payment_pending"}`
 *  when the commercial is still owed commission — the raw axios error is
 *  surfaced so the caller can read `err.response.data.detail` / `.code`. */
export function useDeleteCommercial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(ApiPaths.partnerCommercialDelete(id))).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-commercials"] });
    },
  });
}

/** Reseller disassociates a commercial — its orgs move to NexBasira-direct.
 *  `effective_date` (ISO date) is optional; omit to apply now. Same 409
 *  payment-pending shape as delete; the axios error is surfaced. */
export function useDisassociateCommercial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      effective_date,
    }: {
      id: string;
      effective_date?: string;
    }) =>
      (
        await api.post(
          ApiPaths.partnerCommercialDisassociate(id),
          effective_date ? { effective_date } : {},
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-commercials"] });
    },
  });
}

export type CreateCommercialInput = {
  name: string;
  email: string;
  phone?: string;
  is_company?: boolean;
  legal_name?: string;
  registration_number?: string;
  vat_id?: string;
  address_line?: string;
  city?: string;
  postal_code?: string;
  address_country?: string;
  commission_model?: "percentage" | "fixed";
  rate_base_pct?: string;
  rate_session_pct?: string;
  rate_signature_pct?: string;
  fixed_base_eur?: string;
  fixed_session_eur?: string;
  fixed_signature_eur?: string;
  commission_duration?: string;
};

export function useCreateCommercial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCommercialInput) =>
      (
        await api.post<{ id: string; referral_code: string }>(
          ApiPaths.partnerCommercials,
          input,
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-commercials"] });
    },
  });
}

export function useConnectOnboard() {
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ url: string }>(ApiPaths.partnerConnectOnboard)).data,
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
  });
}

export function useConnectRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ payouts_enabled: boolean }>(
        ApiPaths.partnerConnectRefresh,
      )).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-me"] });
    },
  });
}

export type CreatePartnerOrgInput = {
  name: string;
  admin_email: string;
  admin_password: string;
  country?: string;
  phone?: string;
  address?: import("./types").OrgAddress;
  contacts?: import("./types").OrgContact[];
  custom_price_per_session_eur?: string | null;
  commercial_id?: string | null;
  included_signatures_per_session?: number;
  included_signature_level?: string;
  price_per_signature_overflow_eur?: string | null;
  commissionable_price_per_session_eur?: string | null;
  legal_name?: string;
  registration_number?: string;
  naf_code?: string;
  vat_id?: string;
};

/** Editable fields on an existing org (PATCH — all optional). */
export type UpdatePartnerOrgInput = {
  name?: string;
  phone?: string;
  address?: import("./types").OrgAddress;
  contacts?: import("./types").OrgContact[];
  custom_price_per_session_eur?: string | null;
  included_signatures_per_session?: number;
  included_signature_level?: string;
  price_per_signature_overflow_eur?: string | null;
  commissionable_price_per_session_eur?: string | null;
  legal_name?: string;
  registration_number?: string;
  naf_code?: string;
  vat_id?: string;
};

/** SIRENE (INSEE) company lookup result — pre-fills the org form. */
export type CompanyLookupResult = {
  legal_name: string;
  siren: string;
  siret: string;
  naf_code?: string;
  address: import("./types").OrgAddress;
};

/** Look up a French company by SIRET/SIREN. Throws on 400/404/503 — the
 *  caller maps the status to a friendly message. Not a hook (imperative,
 *  fired on a button click), so we call the API directly. */
export async function lookupCompany(
  identifier: string,
): Promise<CompanyLookupResult> {
  const { data } = await api.get<CompanyLookupResult>(
    ApiPaths.partnerCompanyLookup,
    { params: { siret: identifier } },
  );
  return data;
}

export function useCreatePartnerOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePartnerOrgInput) =>
      (await api.post<PartnerOrg>(ApiPaths.partnerOrgs, input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PARTNER_ORGS_KEY });
      void qc.invalidateQueries({ queryKey: PARTNER_ME_KEY });
    },
  });
}

/** A wholesale reseller's P&L + client invoices to reconcile (P4.5). */
export function usePartnerEconomics() {
  return useQuery<import("./types").ResellerEconomics>({
    queryKey: ["partner-economics"],
    queryFn: async () =>
      (await api.get<import("./types").ResellerEconomics>(ApiPaths.partnerEconomics))
        .data,
    enabled: !!tokenStore.getAccess(),
  });
}

/** Reseller marks a client wholesale invoice paid (collected off-platform). */
export function useMarkClientInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string) =>
      (await api.post(ApiPaths.partnerInvoiceMarkPaid(invoiceId))).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-economics"] });
    },
  });
}

/* ---- Signature packs (reseller grants / requests inbox) ---- */

export function usePartnerSignaturePacks(enabled = true) {
  return useQuery<import("./types").PartnerPackOption[]>({
    queryKey: ["partner-signature-packs"],
    queryFn: async () =>
      (
        await api.get<{ packs: import("./types").PartnerPackOption[] }>(
          ApiPaths.partnerSignaturePacks,
        )
      ).data.packs,
    enabled: enabled && !!tokenStore.getAccess(),
    staleTime: 300_000,
  });
}

export function usePartnerPackRequests(enabled = true) {
  return useQuery<import("./types").SignaturePackRequest[]>({
    queryKey: ["partner-pack-requests"],
    queryFn: async () =>
      (
        await api.get<import("./types").SignaturePackRequest[]>(
          ApiPaths.partnerSignaturePackRequests,
        )
      ).data,
    enabled: enabled && !!tokenStore.getAccess(),
  });
}

export function useGrantSignaturePack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      orgId: string;
      level: "l1" | "l2" | "l3";
      quantity: number;
      setups?: number;
      request_id?: string;
    }) =>
      (
        await api.post(ApiPaths.partnerSignaturePackGrant(v.orgId), {
          level: v.level,
          quantity: v.quantity,
          setups: v.setups ?? 0,
          request_id: v.request_id,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-pack-requests"] });
      void qc.invalidateQueries({ queryKey: PARTNER_ORGS_KEY });
    },
  });
}

export function useDeclinePackRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(ApiPaths.partnerSignaturePackRequestDecline(id), {}))
        .data,
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["partner-pack-requests"] }),
  });
}

/* --------------- Team members (partner RBAC) --------------- */

const PARTNER_MEMBERS_KEY = ["partner-members"] as const;

export function usePartnerMembers(enabled = true) {
  return useQuery<{
    members: PartnerMember[];
    pending_invites: PartnerPendingInvite[];
  }>({
    queryKey: PARTNER_MEMBERS_KEY,
    queryFn: async () => (await api.get(ApiPaths.partnerMembers)).data,
    enabled: enabled && !!tokenStore.getAccess(),
    staleTime: 30_000,
  });
}

export function useInvitePartnerMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      role: PartnerRole;
      first_name?: string;
      last_name?: string;
    }) => (await api.post(ApiPaths.partnerMembers, input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTNER_MEMBERS_KEY }),
  });
}

export function useSetPartnerMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: PartnerRole }) =>
      (await api.patch(ApiPaths.partnerMemberDetail(userId), { role })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTNER_MEMBERS_KEY }),
  });
}

export function useRemovePartnerMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) =>
      (await api.delete(ApiPaths.partnerMemberDetail(userId))).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTNER_MEMBERS_KEY }),
  });
}

/* ---- Account-opening emails (resend / invite) ---- */

/** (Re)send a commercial's account-opening email. Refreshes the
 *  commercials breakdown so has_login / invite_pending reflect the send. */
export function useInviteCommercial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<{ status: string; email: string; expires_at?: string }>(
          ApiPaths.partnerCommercialInvite(id),
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-commercials"] });
    },
  });
}

/** Resend a managed org's admin account-opening invite. */
export function useResendPartnerOrgInvite() {
  return useMutation({
    mutationFn: async (orgId: string) =>
      (
        await api.post<{ status: string; email: string }>(
          ApiPaths.partnerOrgResendInvite(orgId),
        )
      ).data,
  });
}

/** Reset a managed org's admin password to a freshly generated one.
 * The response carries the plaintext ONCE — the caller is responsible
 * for showing it (copy-to-clipboard) and never persisting it. */
export function useResetPartnerOrgPassword() {
  return useMutation({
    mutationFn: async (orgId: string) =>
      (
        await api.post<{ email: string; new_password: string }>(
          ApiPaths.partnerOrgResetPassword(orgId),
        )
      ).data,
  });
}

/* --------------- Commercial team (flat, no roles) --------------- */

export type CommercialTeamMember = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_self: boolean;
};

export type CommercialTeamPendingInvite = {
  id: string;
  email: string;
  invited_at: string;
};

const COMMERCIAL_TEAM_KEY = ["commercial-team"] as const;

export function useCommercialTeam(enabled = true) {
  return useQuery<{
    members: CommercialTeamMember[];
    pending_invites: CommercialTeamPendingInvite[];
  }>({
    queryKey: COMMERCIAL_TEAM_KEY,
    queryFn: async () => (await api.get(ApiPaths.partnerCommercialTeam)).data,
    enabled: enabled && !!tokenStore.getAccess(),
    staleTime: 30_000,
  });
}

export function useInviteCommercialTeammate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; name?: string }) =>
      (
        await api.post<{ status: string; email: string }>(
          ApiPaths.partnerCommercialTeam,
          input,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: COMMERCIAL_TEAM_KEY }),
  });
}

export function useRemoveCommercialTeammate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) =>
      (await api.delete(ApiPaths.partnerCommercialTeamMember(userId))).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: COMMERCIAL_TEAM_KEY }),
  });
}
